import React from 'react';

const FeaturesSection: React.FC = () => {
  return (
    <section className="w-full py-16 px-4">
      <div className="max-w-6xl mx-auto">
        {/* Section Header */}
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Why Choose PixelShift?
          </h2>
          <p className="text-lg text-gray-400">
            The most powerful free image converter - no compromises
          </p>
        </div>

        {/* Features Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
          {/* Feature 1: Batch Processing */}
          <div className="bg-gray-800 rounded-xl p-6 border border-gray-700 hover:border-brand-primary transition-all duration-200">
            <div className="w-12 h-12 bg-brand-primary/20 rounded-lg flex items-center justify-center mb-4">
              <svg className="w-6 h-6 text-brand-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
              </svg>
            </div>
            <h3 className="text-xl font-bold text-white mb-2">Batch Processing</h3>
            <p className="text-gray-400">
              Convert up to 10 images at once. Free tier includes batch conversion - no other free tool offers this!
            </p>
          </div>

          {/* Feature 2: Quality Control */}
          <div className="bg-gray-800 rounded-xl p-6 border border-gray-700 hover:border-brand-primary transition-all duration-200">
            <div className="w-12 h-12 bg-brand-primary/20 rounded-lg flex items-center justify-center mb-4">
              <svg className="w-6 h-6 text-brand-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
              </svg>
            </div>
            <h3 className="text-xl font-bold text-white mb-2">Quality Slider</h3>
            <p className="text-gray-400">
              Professional-grade compression control. Adjust quality from 1-100% to balance file size and quality.
            </p>
          </div>

          {/* Feature 3: Custom Resize */}
          <div className="bg-gray-800 rounded-xl p-6 border border-gray-700 hover:border-brand-primary transition-all duration-200">
            <div className="w-12 h-12 bg-brand-primary/20 rounded-lg flex items-center justify-center mb-4">
              <svg className="w-6 h-6 text-brand-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
              </svg>
            </div>
            <h3 className="text-xl font-bold text-white mb-2">Custom Resize</h3>
            <p className="text-gray-400">
              Resize images during conversion. Set custom dimensions - save time by doing both in one step.
            </p>
          </div>

          {/* Feature 4: HEIC Support */}
          <div className="bg-gray-800 rounded-xl p-6 border border-gray-700 hover:border-brand-primary transition-all duration-200">
            <div className="w-12 h-12 bg-brand-primary/20 rounded-lg flex items-center justify-center mb-4">
              <svg className="w-6 h-6 text-brand-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
              </svg>
            </div>
            <h3 className="text-xl font-bold text-white mb-2">iPhone Photos (HEIC)</h3>
            <p className="text-gray-400">
              Convert iPhone photos seamlessly. Full HEIC support - perfect for sharing photos with Android users.
            </p>
          </div>

          {/* Feature 5: Privacy First */}
          <div className="bg-gray-800 rounded-xl p-6 border border-gray-700 hover:border-brand-primary transition-all duration-200">
            <div className="w-12 h-12 bg-brand-primary/20 rounded-lg flex items-center justify-center mb-4">
              <svg className="w-6 h-6 text-brand-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
            </div>
            <h3 className="text-xl font-bold text-white mb-2">100% Private</h3>
            <p className="text-gray-400">
              All processing happens in your browser. Your images never leave your device - no uploads, no tracking.
            </p>
          </div>

          {/* Feature 6: No Limits */}
          <div className="bg-gray-800 rounded-xl p-6 border border-gray-700 hover:border-brand-primary transition-all duration-200">
            <div className="w-12 h-12 bg-brand-primary/20 rounded-lg flex items-center justify-center mb-4">
              <svg className="w-6 h-6 text-brand-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
            </div>
            <h3 className="text-xl font-bold text-white mb-2">No Limits, No Ads</h3>
            <p className="text-gray-400">
              Unlimited conversions. No daily caps, no hidden fees, no account required. Free means free - forever.
            </p>
          </div>
        </div>

        {/* Comparison Section */}
        <div className="bg-gradient-to-r from-brand-primary/10 to-purple-600/10 rounded-2xl p-8 border border-brand-primary/30">
          <h3 className="text-2xl font-bold text-white text-center mb-6">
            PixelShift vs Others
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Others */}
            <div className="bg-gray-900/50 rounded-xl p-6">
              <h4 className="text-lg font-semibold text-gray-400 mb-4 flex items-center">
                <span className="text-red-400 mr-2">✗</span>
                Other "Free" Converters
              </h4>
              <ul className="space-y-2 text-gray-400 text-sm">
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  <span>1-2 files max per conversion</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  <span>Daily conversion limits (10-25/day)</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  <span>Ads and popups everywhere</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  <span>No quality control</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  <span>Upload to servers (privacy risk)</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  <span>No HEIC support</span>
                </li>
              </ul>
            </div>

            {/* PixelShift */}
            <div className="bg-brand-primary/20 rounded-xl p-6 border border-brand-primary">
              <h4 className="text-lg font-semibold text-white mb-4 flex items-center">
                <span className="text-green-400 mr-2">✓</span>
                PixelShift Free
              </h4>
              <ul className="space-y-2 text-gray-200 text-sm">
                <li className="flex items-start">
                  <span className="text-green-400 mr-2">✓</span>
                  <span><strong>10 files at once</strong> - batch processing</span>
                </li>
                <li className="flex items-start">
                  <span className="text-green-400 mr-2">✓</span>
                  <span><strong>Unlimited conversions</strong> - no daily caps</span>
                </li>
                <li className="flex items-start">
                  <span className="text-green-400 mr-2">✓</span>
                  <span><strong>Zero ads</strong> - clean interface</span>
                </li>
                <li className="flex items-start">
                  <span className="text-green-400 mr-2">✓</span>
                  <span><strong>Quality slider</strong> - full control</span>
                </li>
                <li className="flex items-start">
                  <span className="text-green-400 mr-2">✓</span>
                  <span><strong>100% private</strong> - client-side only</span>
                </li>
                <li className="flex items-start">
                  <span className="text-green-400 mr-2">✓</span>
                  <span><strong>WEBP + HEIC</strong> - iPhone photos</span>
                </li>
              </ul>
            </div>
          </div>
          
          <p className="text-center text-gray-300 mt-6">
            🏆 <strong className="text-white">Best free tier on the internet</strong> - no other tool comes close
          </p>
        </div>
      </div>
    </section>
  );
};

export default FeaturesSection;

